/**
 * The `cypher_write` agent-action view capability — the render half of the
 * Neo4j write-approval card. Holds the cypher-specific presentation (the
 * change-kind badge, the cypher / evidence proposal body, and the applied
 * /rejected settled summary) that the generic `AgentActionApprovalPanel`
 * delegates to. Registered as a side effect on import.
 *
 * The JSX here is lifted verbatim from the original
 * `CypherWriteProposalPanel` so the rendered card is byte-identical.
 */
import type { InFlightAgentActionProposal } from '@axilon-apps/domains-ontology-ui-state'
import { changeKindLabel } from '../changeKindLabel'
import { GraphMutationSettledSummary } from './graphMutationViewShared'
import { registerAgentActionView, type AgentActionViewCapability } from './viewRegistry'

type CypherWriteProposal = Extract<InFlightAgentActionProposal, { kind: 'cypher_write' }>

function isCypherWrite(p: InFlightAgentActionProposal): p is CypherWriteProposal {
  return p.kind === 'cypher_write'
}

function readProposalParam(proposal: CypherWriteProposal, key: string): string | null {
  const value = proposal.params[key]
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

function EvidenceProposalDetail({ proposal }: { proposal: CypherWriteProposal }) {
  const nodeLabel =
    readProposalParam(proposal, 'displayName') ??
    readProposalParam(proposal, 'resolvedNodeId') ??
    readProposalParam(proposal, 'nodeId')
  const note = readProposalParam(proposal, 'note')
  const valid = proposal.params.valid
  const validity =
    valid === false ? 'Mark invalid' : valid === true ? 'Mark valid' : 'Record note only'

  return (
    <div className="rounded-xl border border-line/70 bg-muted/30 p-2 text-11 text-foreground">
      <div className="grid gap-1.5">
        <div className="flex items-center gap-1.5">
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-10 font-medium text-primary">
            {validity}
          </span>
        </div>
        {nodeLabel ? (
          <div className="break-words">
            <span className="text-muted-foreground">Node:</span> {nodeLabel}
          </div>
        ) : null}
        {note ? (
          <div className="break-words">
            <span className="text-muted-foreground">Evidence:</span> {note}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function ProposalDetail({ proposal }: { proposal: CypherWriteProposal }) {
  if (proposal.changeKind === 'node_evidence') {
    return <EvidenceProposalDetail proposal={proposal} />
  }
  return (
    <pre className="overflow-x-auto rounded-xl border border-line/70 bg-muted/30 p-2 font-mono text-10 whitespace-pre-wrap break-words text-foreground">
      {proposal.cypher}
    </pre>
  )
}

export const cypherWriteViewCapability: AgentActionViewCapability = {
  kind: 'cypher_write',
  badgeLabel(proposal) {
    // Capture the discriminator before narrowing so the fallback stays a
    // plain string (the union has a single member today, which would make
    // the narrowed `else` branch `never`).
    const kind: string = proposal.kind
    return isCypherWrite(proposal) ? changeKindLabel(proposal.changeKind) : kind
  },
  renderDetail(proposal) {
    if (!isCypherWrite(proposal)) return null
    return <ProposalDetail proposal={proposal} />
  },
  renderSettledSummary(proposal) {
    if (!isCypherWrite(proposal)) return null
    return <GraphMutationSettledSummary proposal={proposal} />
  }
}

registerAgentActionView(cypherWriteViewCapability)
